import { type ReactNode, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useResetRedemption } from '../hooks/use-reset-redemption';
import { ReasonInput } from './status-override-inputs';

type Props = { memberId: string; onDone?: () => void };

function CardHeader(): ReactNode {
  return (
    <div>
      <h3 className="text-sm font-medium text-text-primary">
        Reactivar redención
      </h3>
      <p className="mt-1 text-xs leading-relaxed text-text-secondary">
        Este usuario ya usó su redención. Solo úsalo si fue un error o si la
        dirección ha decidido darle otra oportunidad.
      </p>
    </div>
  );
}

type ConfirmRowProps = {
  checked: boolean;
  onChange: (v: boolean) => void;
};

function ConfirmRow(p: ConfirmRowProps): ReactNode {
  return (
    <label className="flex items-center gap-xs text-xs text-warning">
      <input
        type="checkbox"
        checked={p.checked}
        onChange={(e) => p.onChange(e.target.checked)}
      />
      He confirmado que quiero reactivar redención
    </label>
  );
}

// Fase 28 (28.8.1): admin "gift" card — visible only when the member has
// already used their one-shot redemption. Reactivates eligibility so the
// next ban will offer them the Game Over redemption block again. Reason
// is required (logged server-side for audit).
export function RedemptionResetCard(p: Props): ReactNode {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const reset = useResetRedemption();
  const trimmed = reason.trim();
  const can = confirmed && trimmed.length > 0 && !reset.isPending;
  const onClick = (): void =>
    reset.mutate(
      { id: p.memberId, reason: trimmed },
      { onSuccess: () => p.onDone?.() }
    );

  return (
    <div className="space-y-sm rounded-lg border border-warning/30 bg-warning/5 p-base">
      <CardHeader />
      <ReasonInput value={reason} onChange={setReason} />
      <ConfirmRow checked={confirmed} onChange={setConfirmed} />
      <Button size="sm" disabled={!can} onClick={onClick}>
        {reset.isPending ? 'Reactivando...' : 'Reactivar redención'}
      </Button>
    </div>
  );
}
